<script setup>
import { ref, watch, onMounted, nextTick, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useTaskActions } from 'dashboard/modules/tasks/composables/useTaskActions';
import TasksAPI from 'dashboard/api/tasks';
import TaskList from './components/TaskList.vue';
import TaskCalendarView from './components/TaskCalendarView.vue';
import TaskWeekView from './components/TaskWeekView.vue';
import TaskFilters from './components/TaskFilters.vue';
import TaskModal from './components/TaskModal.vue';
import TasksSidebar from './components/TasksSidebar.vue';
import TaskSort from './components/TaskSort.vue';
import TaskBulkActions from './components/TaskBulkActions.vue';
import KanbanOpportunityModal from 'dashboard/modules/kanban/KanbanOpportunityModal.vue';
import FlexibleRecurrencePopover from './components/FlexibleRecurrencePopover.vue';
import TaskProgressBar from './components/TaskProgressBar.vue';
import AgendaMeetingModal from 'dashboard/modules/agenda/components/AgendaMeetingModal.vue';
import { useBreakpoints, breakpointsTailwind } from '@vueuse/core';
import { useRoute, useRouter } from 'vue-router';

const { t } = useI18n();
const store = useStore();
const { updateTask } = useTaskActions();
const route = useRoute();
const router = useRouter();
const currentUser = useMapGetter('getCurrentUser');

const breakpoints = useBreakpoints(breakpointsTailwind);
const isMobile = breakpoints.smaller('md');
const mobileTab = ref('list'); // 'list' | 'agenda'

const currentUserId = currentUser.value?.id;
const SORT_STORAGE_KEY = `tasks_sort_pref_${currentUserId}`;
const FILTERS_STORAGE_KEY = `tasks_filters_pref_${currentUserId}`;

const activeTabs = ref([]);
const activeView = ref('list'); // 'list' | 'week' | 'calendar'
// ?task_id= abre a tarefa direto (link de notificação ou compartilhado)
const selectedTaskId = ref(Number(route.query.task_id) || null);
const openTaskWithWarning = ref(false);
const showNewTaskForm = ref(false);
const selectedMeetingId = ref(null);
const showNewMeetingModal = ref(false);

// seleção em massa
const selectionMode = ref(false);
const selectedIds = ref([]);

function toggleSelectionMode() {
  selectionMode.value = !selectionMode.value;
  selectedIds.value = [];
}

function onTaskSelected(id) {
  const idx = selectedIds.value.indexOf(id);
  if (idx === -1) selectedIds.value = [...selectedIds.value, id];
  else selectedIds.value = selectedIds.value.filter(i => i !== id);
}

const dateFilter = ref({});
const selectedDayLabel = ref('');
const sidebarRef = ref(null);
const taskFiltersRef = ref(null);
const taskSortRef = ref(null);
const searchQuery = ref('');

const savedFilters = (() => {
  try {
    return JSON.parse(localStorage.getItem(FILTERS_STORAGE_KEY)) ?? null;
  } catch {
    return null;
  }
})();
const activeFilters = ref(savedFilters ?? {});

// sort — persiste no localStorage
const savedSort = (() => {
  try {
    return JSON.parse(localStorage.getItem(SORT_STORAGE_KEY)) ?? {};
  } catch {
    return {};
  }
})();
const sortBy = ref(savedSort.sortBy ?? 'due_date');
const sortDir = ref(savedSort.sortDir ?? 'asc');

function localDateStr(date = new Date()) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function tabFilters(tab) {
  const today = localDateStr();
  const yesterday = (() => {
    const d = new Date();
    d.setDate(d.getDate() - 1);
    return localDateStr(d);
  })();
  const startOfToday = `${today}T00:00:00`;
  const endOfYesterday = `${yesterday}T23:59:59`;
  if (tab === 'today')
    return {
      due_date_from: `${today}T00:00:00`,
      due_date_to: `${today}T23:59:59`,
      status: 'pending',
    };
  if (tab === 'overdue')
    return { due_date_to: endOfYesterday, status: 'pending' };
  if (tab === 'upcoming')
    return { due_date_from: startOfToday, status: 'pending' };
  if (tab === 'no_date') return { no_due_date: true };
  return {};
}

function buildTabsParams() {
  if (activeTabs.value.length === 0) return {};
  return { tabs: activeTabs.value, today: localDateStr() };
}

const progressCompleted = ref(0);
const progressTotal = ref(0);

async function fetchProgressCounts() {
  const filtersWithoutStatus = { ...activeFilters.value };
  delete filtersWithoutStatus.status;

  const baseParams = {
    ...buildTabsParams(),
    ...filtersWithoutStatus,
    ...(activeTabs.value.length > 0 ? {} : dateFilter.value),
  };

  try {
    const { data } = await TasksAPI.list({ ...baseParams, count_only: true });
    progressCompleted.value = data.meta.completed_count;
    progressTotal.value = data.meta.total_count;
  } catch {
    // mantém os valores anteriores em caso de erro
  }
}

function persistSort() {
  localStorage.setItem(
    SORT_STORAGE_KEY,
    JSON.stringify({ sortBy: sortBy.value, sortDir: sortDir.value })
  );
}

function onSortByChange(val) {
  sortBy.value = val;
  sortDir.value = 'asc';
  persistSort();
}

let suppressSortWatch = false;

function onDragReorder() {
  if (sortBy.value !== 'custom') {
    suppressSortWatch = true;
    sortBy.value = 'custom';
    sortDir.value = 'asc';
    persistSort();
    // nextTick garante que o watch já rodou antes de liberar
    nextTick(() => {
      suppressSortWatch = false;
    });
  }
}

function onSortDirChange(val) {
  sortDir.value = val;
  persistSort();
}

// contadores por tab
const tabCounts = ref({
  today: null,
  overdue: null,
  upcoming: null,
  no_date: null,
});

async function fetchTabCounts() {
  const tabs = ['today', 'overdue', 'upcoming', 'no_date'];
  const results = await Promise.allSettled(
    tabs.map(tab => TasksAPI.list({ ...tabFilters(tab), count_only: true }))
  );
  results.forEach((result, i) => {
    if (result.status === 'fulfilled') {
      tabCounts.value[tabs[i]] = result.value.data.meta.total_count;
    }
  });
}

function buildSortParams() {
  if (sortBy.value === 'custom') return {};
  let dir = sortDir.value;
  if (sortBy.value === 'priority') {
    dir = sortDir.value === 'asc' ? 'desc' : 'asc';
  }
  return { sort_by: sortBy.value, sort_dir: dir };
}

async function loadTasks() {
  const q = searchQuery.value.trim();
  await store.dispatch('tasks/fetchTasks', {
    ...buildTabsParams(),
    ...activeFilters.value,
    ...(activeTabs.value.length > 0 ? {} : dateFilter.value),
    ...buildSortParams(),
    ...(q ? { q } : {}),
  });
  fetchProgressCounts();
}

let searchDebounce = null;
function onSearchInput() {
  clearTimeout(searchDebounce);
  searchDebounce = setTimeout(loadTasks, 300);
}

watch([sortBy, sortDir], () => {
  if (suppressSortWatch) return;
  loadTasks();
});

watch(activeView, val => {
  if (val === 'list') loadTasks();
});

const showBulkDropdown = ref(false);
const bulkBtnRef = ref(null);
const bulkContainerRef = ref(null);
const bulkDropdownPos = ref({ top: 0, right: 0 });

function calcBulkDropdownPos() {
  if (!bulkBtnRef.value) return;
  const rect = bulkBtnRef.value.getBoundingClientRect();
  bulkDropdownPos.value = {
    top: rect.bottom + 4,
    right: window.innerWidth - rect.right,
  };
}

function onDocMousedown(e) {
  if (!showBulkDropdown.value) return;
  if (bulkContainerRef.value && bulkContainerRef.value.contains(e.target))
    return;
  const teleported = document.querySelectorAll('[data-bulk-dropdown]');
  if ([...teleported].some(el => el.contains(e.target))) return;
  showBulkDropdown.value = false;
}

onMounted(() => document.addEventListener('mousedown', onDocMousedown));
onUnmounted(() => document.removeEventListener('mousedown', onDocMousedown));

function toggleBulkDropdown() {
  if (!selectionMode.value) {
    toggleSelectionMode();
    return;
  }
  if (selectedIds.value.length === 0) {
    toggleSelectionMode();
    return;
  }
  if (!showBulkDropdown.value) calcBulkDropdownPos();
  showBulkDropdown.value = !showBulkDropdown.value;
}

function onBulkDone() {
  selectionMode.value = false;
  selectedIds.value = [];
  showBulkDropdown.value = false;
  loadTasks();
  fetchTabCounts();
}

function onBulkClose() {
  showBulkDropdown.value = false;
}

function onBulkCancel() {
  selectionMode.value = false;
  selectedIds.value = [];
  showBulkDropdown.value = false;
}

function onDateSelected(range) {
  activeTabs.value = [];
  if (!range) {
    dateFilter.value = {};
    selectedDayLabel.value = '';
  } else {
    dateFilter.value = { due_date_from: range.from, due_date_to: range.to };
    const from = new Date(range.from);
    const to = new Date(range.to);
    const sameDay =
      from.getDate() === to.getDate() &&
      from.getMonth() === to.getMonth() &&
      from.getFullYear() === to.getFullYear();
    if (sameDay) {
      selectedDayLabel.value = from.toLocaleDateString('pt-BR', {
        day: 'numeric',
        month: 'short',
      });
    } else {
      const sameMonth =
        from.getMonth() === to.getMonth() &&
        from.getFullYear() === to.getFullYear();
      if (sameMonth) {
        const month = from.toLocaleDateString('pt-BR', { month: 'short' });
        selectedDayLabel.value = `${from.getDate()} – ${to.getDate()} ${month}`;
      } else {
        const fmtFrom = from.toLocaleDateString('pt-BR', {
          day: 'numeric',
          month: 'short',
        });
        const fmtTo = to.toLocaleDateString('pt-BR', {
          day: 'numeric',
          month: 'short',
        });
        selectedDayLabel.value = `${fmtFrom} – ${fmtTo}`;
      }
    }
  }
  loadTasks();
}

function clearDateFilter() {
  dateFilter.value = {};
  selectedDayLabel.value = '';
  sidebarRef.value?.clearSelection();
  loadTasks();
}

function onTabChange(tab) {
  const idx = activeTabs.value.indexOf(tab);
  if (idx === -1) {
    activeTabs.value = [...activeTabs.value, tab];
  } else {
    activeTabs.value = activeTabs.value.filter(tb => tb !== tab);
  }
  dateFilter.value = {};
  selectedDayLabel.value = '';
  sidebarRef.value?.clearSelection();
  loadTasks();
}

function onFilterChange(filters) {
  activeFilters.value = filters;
  localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
  loadTasks();
}

const openedCard = ref(null);
const flexiblePopoverTask = ref(null);

function onTaskOpenWithWarning(task) {
  openTaskWithWarning.value = true;
  selectedTaskId.value = task.id;
}

async function onTaskComplete(task) {
  const newStatus = task.status === 'completed' ? 'pending' : 'completed';
  if (newStatus === 'completed') {
    const checklistItems = task.checklist_items ?? [];
    if (checklistItems.length > 0 && checklistItems.some(i => !i.completed)) {
      onTaskOpenWithWarning(task);
      return;
    }
  }
  const recurrenceType = task.recurrence_type;
  const taskSnapshot = { ...task };
  await updateTask({
    taskId: task.id,
    status: newStatus,
    completed_at: newStatus === 'completed' ? new Date().toISOString() : null,
  });
  fetchTabCounts();
  fetchProgressCounts();

  if (newStatus === 'completed' && recurrenceType === 'flexible') {
    flexiblePopoverTask.value = taskSnapshot;
  }
}

function onFlexibleScheduled() {
  flexiblePopoverTask.value = null;
  loadTasks();
  fetchTabCounts();
}

function onFlexibleExpand(newTask) {
  flexiblePopoverTask.value = null;
  loadTasks();
  fetchTabCounts();
  selectedTaskId.value = newTask.id;
}

async function onOpenCard(card) {
  await store.dispatch('kanban/fetchPipelines');
  openedCard.value = card;
}

function closeTaskModal() {
  selectedTaskId.value = null;
  openTaskWithWarning.value = false;
  if (route.query.task_id) {
    const { task_id: _taskId, ...query } = route.query;
    router.replace({ query });
  }
}

watch(
  () => route.query.task_id,
  id => {
    if (id) selectedTaskId.value = Number(id);
  }
);

onMounted(async () => {
  await Promise.all([
    store.dispatch('agents/get'),
    store.dispatch('labels/get'),
  ]);
  if (savedFilters === null) {
    const userId = currentUser.value?.id;
    if (userId) {
      activeFilters.value = { assignee_id: [userId], status: 'pending' };
      localStorage.setItem(
        FILTERS_STORAGE_KEY,
        JSON.stringify(activeFilters.value)
      );
    }
    sortBy.value = 'due_date';
    sortDir.value = 'asc';
    persistSort();
  }
  loadTasks();
  fetchTabCounts();
});
</script>

<template>
  <div class="flex flex-col md:flex-row h-full w-full overflow-hidden">
    <!-- Abas mobile: Lista / Agenda -->
    <div
      v-if="isMobile"
      class="flex items-center border-b border-n-weak flex-shrink-0"
    >
      <button
        class="flex-1 text-sm font-medium py-2.5 border-b-2 transition-colors"
        :class="
          mobileTab === 'list'
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-10'
        "
        @click="mobileTab = 'list'"
      >
        {{ t('TASKS.HEADER') }}
      </button>
      <button
        class="flex-1 text-sm font-medium py-2.5 border-b-2 transition-colors"
        :class="
          mobileTab === 'agenda'
            ? 'border-n-brand text-n-brand'
            : 'border-transparent text-n-slate-10'
        "
        @click="mobileTab = 'agenda'"
      >
        {{ t('TASKS.SIDEBAR.AGENDA_TAB') }}
      </button>
    </div>
    <!-- Coluna principal: header + tabs + filtros + lista -->
    <div
      v-if="!isMobile || mobileTab === 'list'"
      class="flex flex-col flex-1 overflow-hidden"
    >
      <!-- Header: Tabs + busca + botão novo -->
      <div
        class="flex items-center justify-between border-b border-n-weak px-4 md:px-8 py-2.5 flex-shrink-0 gap-3 md:gap-4 flex-wrap"
      >
        <div
          v-if="activeView === 'list'"
          class="flex items-center gap-4 min-w-0"
        >
          <span class="text-xl font-semibold text-n-slate-12 flex-shrink-0">{{
            t('TASKS.HEADER')
          }}</span>
          <TaskProgressBar
            :completed="progressCompleted"
            :total="progressTotal"
          />
        </div>
        <div
          v-else
          class="flex items-center gap-2 w-full lg:w-auto lg:flex-1 min-w-0 overflow-x-auto order-3 lg:order-none"
        >
          <TaskFilters
            ref="taskFiltersRef"
            :initial-filters="activeFilters"
            @filter-change="onFilterChange"
          />
        </div>
        <div class="flex items-center gap-2 flex-shrink-0">
          <!-- Busca: ícone que expande no hover/focus -->
          <div class="group relative flex items-center">
            <div
              class="flex items-center border rounded-lg overflow-hidden transition-all duration-200 group-hover:border-n-weak group-focus-within:border-n-brand"
              :class="searchQuery ? 'border-n-weak' : 'border-transparent'"
            >
              <input
                v-model="searchQuery"
                :placeholder="t('TASKS.FILTERS.SEARCH')"
                class="text-sm bg-transparent text-n-slate-12 placeholder:text-n-slate-9 outline-none transition-all duration-200 py-1.5"
                :class="
                  searchQuery
                    ? 'w-40 px-2'
                    : 'w-0 px-0 group-hover:w-40 group-hover:px-2 group-focus-within:w-40 group-focus-within:px-2'
                "
                @input="onSearchInput"
              />
              <span
                class="i-lucide-search size-4 text-n-slate-10 flex-shrink-0 mr-1.5 group-hover:text-n-slate-12 group-focus-within:text-n-brand transition-colors"
              />
            </div>
          </div>
          <!-- Toggle Lista / Calendário -->
          <div
            class="flex items-center gap-0.5 p-0.5 border border-n-weak rounded-lg flex-shrink-0"
          >
            <button
              class="px-2.5 py-1 text-sm rounded-md transition-colors"
              :class="
                activeView === 'list'
                  ? 'bg-n-alpha-2 text-n-brand font-medium'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeView = 'list'"
            >
              {{ t('TASKS.CALENDAR_VIEW.TOGGLE_LIST') }}
            </button>
            <button
              class="px-2.5 py-1 text-sm rounded-md transition-colors"
              :class="
                activeView === 'week'
                  ? 'bg-n-alpha-2 text-n-brand font-medium'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeView = 'week'"
            >
              {{ t('TASKS.CALENDAR_VIEW.TOGGLE_WEEK') }}
            </button>
            <button
              class="px-2.5 py-1 text-sm rounded-md transition-colors"
              :class="
                activeView === 'calendar'
                  ? 'bg-n-alpha-2 text-n-brand font-medium'
                  : 'text-n-slate-11 hover:text-n-slate-12'
              "
              @click="activeView = 'calendar'"
            >
              {{ t('TASKS.CALENDAR_VIEW.TOGGLE_CALENDAR') }}
            </button>
          </div>
          <button
            class="flex items-center justify-center size-8 bg-n-brand text-white rounded-lg hover:brightness-110"
            :title="t('TASKS.NEW_TASK')"
            @click="showNewTaskForm = true"
          >
            <span class="i-lucide-plus size-4" />
          </button>
        </div>
      </div>

      <!-- Filtros + Sort -->
      <div
        v-if="activeView === 'list'"
        class="flex items-center gap-2 px-8 py-3 border-b border-n-weak flex-shrink-0 overflow-x-auto md:overflow-visible md:flex-wrap md:justify-between"
      >
        <div class="flex items-center gap-2 flex-shrink-0 md:flex-wrap">
          <TaskFilters
            ref="taskFiltersRef"
            :initial-filters="activeFilters"
            @filter-change="onFilterChange"
            @open="taskSortRef?.close()"
          />
          <button
            v-if="selectedDayLabel"
            class="flex items-center gap-1 text-sm border rounded-lg px-2.5 py-1.5 transition-colors border-n-brand text-n-brand bg-n-alpha-2 flex-shrink-0"
            @click="clearDateFilter"
          >
            <span class="i-lucide-calendar size-3.5" />
            {{ selectedDayLabel }}
            <span class="i-lucide-x size-3 ml-0.5" />
          </button>
        </div>
        <div
          v-if="activeView === 'list'"
          class="flex items-center gap-2 flex-shrink-0"
        >
          <!-- Botão de ações em massa + dropdown -->
          <div
            ref="bulkContainerRef"
            class="relative flex-shrink-0 flex items-center gap-1"
          >
            <button
              ref="bulkBtnRef"
              class="flex items-center gap-1.5 px-2 h-[30px] border rounded-lg transition-colors flex-shrink-0 outline-none"
              :class="
                selectionMode
                  ? 'border-n-brand text-n-brand bg-n-alpha-2'
                  : 'border-n-weak text-n-slate-11 bg-n-background hover:border-n-container hover:bg-n-alpha-1'
              "
              :title="t('TASKS.BULK.TOGGLE')"
              @click="toggleBulkDropdown"
            >
              <span class="i-lucide-list-checks size-3.5 flex-shrink-0" />
              <span
                v-if="selectionMode"
                class="text-xs font-medium whitespace-nowrap"
              >
                {{ t('TASKS.BULK.SELECTED', { count: selectedIds.length }) }}
              </span>
              <span
                v-if="selectionMode && selectedIds.length > 0"
                class="i-lucide-x size-3.5 ml-0.5 opacity-70 hover:opacity-100"
                @click.stop="onBulkCancel"
              />
            </button>
            <!-- Dropdown de ações em massa -->
            <Teleport to="body">
              <div
                v-if="showBulkDropdown && selectedIds.length > 0"
                data-bulk-dropdown
                class="fixed z-[10010]"
                :style="{
                  top: bulkDropdownPos.top + 'px',
                  right: bulkDropdownPos.right + 'px',
                }"
                @click.stop
              >
                <TaskBulkActions
                  :selected-ids="selectedIds"
                  @done="onBulkDone"
                  @cancel="onBulkCancel"
                  @close="onBulkClose"
                />
              </div>
            </Teleport>
          </div>
          <TaskSort
            ref="taskSortRef"
            :model-value="sortBy"
            :direction="sortDir"
            @update:model-value="onSortByChange"
            @update:direction="onSortDirChange"
            @open="taskFiltersRef?.closeAll()"
          />
        </div>
      </div>

      <!-- Lista -->
      <div
        v-if="activeView === 'list'"
        class="flex-1 overflow-y-auto pb-20 md:pb-0"
      >
        <TaskList
          :allow-drag="!selectionMode"
          :show-inline-create="showNewTaskForm"
          :selection-mode="selectionMode"
          :selected-ids="selectedIds"
          @task-open="task => (selectedTaskId = task.id)"
          @task-complete="onTaskComplete"
          @task-open-with-warning="onTaskOpenWithWarning"
          @task-updated="fetchTabCounts"
          @drag-reorder="onDragReorder"
          @inline-create-done="
            showNewTaskForm = false;
            loadTasks();
          "
          @task-selected="onTaskSelected"
        />
      </div>

      <!-- Semana -->
      <TaskWeekView
        v-if="activeView === 'week'"
        :filters="activeFilters"
        @task-open="taskId => (selectedTaskId = taskId)"
      />

      <!-- Calendário -->
      <TaskCalendarView
        v-else-if="activeView === 'calendar'"
        :filters="activeFilters"
        @task-open="taskId => (selectedTaskId = taskId)"
      />
    </div>

    <!-- Popover de recorrência flexível -->
    <div
      v-if="flexiblePopoverTask"
      class="fixed inset-0 z-20 pointer-events-none"
    >
      <div
        class="pointer-events-auto absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 max-h-[90vh] flex items-center"
      >
        <FlexibleRecurrencePopover
          :task="flexiblePopoverTask"
          @close="flexiblePopoverTask = null"
          @scheduled="onFlexibleScheduled"
          @expand="onFlexibleExpand"
        />
      </div>
    </div>

    <!-- Painel lateral direito -->
    <TasksSidebar
      v-if="activeView === 'list' && (!isMobile || mobileTab === 'agenda')"
      ref="sidebarRef"
      :active-tabs="activeTabs"
      :tab-counts="tabCounts"
      @date-selected="onDateSelected"
      @meeting-open="id => (selectedMeetingId = id)"
      @new-meeting="showNewMeetingModal = true"
      @tab-change="onTabChange"
    />

    <!-- Task modal -->
    <TaskModal
      v-if="selectedTaskId"
      :task-id="selectedTaskId"
      :open-with-checklist-warning="openTaskWithWarning"
      @close="closeTaskModal"
      @open-card="onOpenCard"
      @updated="fetchTabCounts"
      @deleted="loadTasks"
    />

    <!-- Modal de reunião (evento da Agenda) -->
    <AgendaMeetingModal
      v-if="selectedMeetingId || showNewMeetingModal"
      :meeting-id="selectedMeetingId"
      @close="
        selectedMeetingId = null;
        showNewMeetingModal = false;
      "
      @created="showNewMeetingModal = false"
      @updated="selectedMeetingId = null"
      @deleted="selectedMeetingId = null"
      @open-card="onOpenCard"
    />

    <!-- Kanban card modal aberto a partir de uma tarefa -->
    <KanbanOpportunityModal
      v-if="openedCard"
      :card="openedCard"
      @close="openedCard = null"
      @saved="openedCard = null"
      @deleted="openedCard = null"
    />
  </div>
</template>
